import { useState, type FormEvent } from 'react';
import { type BalanceResult, balanceEquation, prettyFormula } from '@chemai/engine';
import { useI18n } from '../../i18n';
import { SaveButton } from '../../components/ui';
import { progress } from '../../lib/progress';

const EXAMPLES = [
  'H2 + O2 -> H2O',
  'Fe + O2 -> Fe2O3',
  'C3H8 + O2 -> CO2 + H2O',
  'Ca(OH)2 + H3PO4 -> Ca3(PO4)2 + H2O',
  'KMnO4 + HCl -> KCl + MnCl2 + Cl2 + H2O',
  'Cu + HNO3 -> Cu(NO3)2 + NO + H2O',
];

function pretty(eq: string) {
  return eq.split(' ').map((tok) => (/[A-Z]/.test(tok) ? prettyFormula(tok.replace(/^(\d+)/, '$1​')) : tok)).join(' ').replace(/​/g, ' ');
}

export function BalancerTool() {
  const { t, errorMessage } = useI18n();
  const [input, setInput] = useState(EXAMPLES[2]);
  const [result, setResult] = useState<BalanceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (eq: string) => {
    try {
      setResult(balanceEquation(eq));
      setError(null);
    } catch (e) {
      setResult(null);
      setError(errorMessage(e));
    }
  };
  const submit = (e: FormEvent) => { e.preventDefault(); run(input); };

  return (
    <div className="sim">
      <section className="panel">
        <form onSubmit={submit} className="balancer-form">
          <label htmlFor="bal-in">{t('bal.input')}</label>
          <div className="field-row">
            <input id="bal-in" value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('bal.placeholder')}
              spellCheck={false} autoCapitalize="off" autoComplete="off" aria-describedby="bal-help" className="mono" />
            <button type="submit" className="btn btn-primary">{t('bal.button')}</button>
          </div>
          <p id="bal-help" className="hint">{t('bal.help')}</p>
        </form>
        <div className="examples">
          <span className="muted">{t('bal.examples')}:</span>
          {EXAMPLES.map((ex) => (
            <button key={ex} type="button" className="chip-btn mono" onClick={() => { setInput(ex); run(ex); }}>{pretty(ex)}</button>
          ))}
        </div>
      </section>

      <section className="panel" aria-live="polite">
        {error && <p className="error" role="alert">{error}</p>}
        {result && (
          <>
            <h3>{t('bal.result')}</h3>
            <p className="equation mono">
              {result.reactants.map((s, i) => (
                <span key={`r${i}`}>{i > 0 && ' + '}<b className="coef">{result.coefficients[i] > 1 ? result.coefficients[i] : ''}</b>{prettyFormula(s.formula)}</span>
              ))}
              {' → '}
              {result.products.map((s, i) => {
                const c = result.coefficients[result.reactants.length + i];
                return <span key={`p${i}`}>{i > 0 && ' + '}<b className="coef">{c > 1 ? c : ''}</b>{prettyFormula(s.formula)}</span>;
              })}
            </p>
            <h4>{t('bal.check')}</h4>
            <div className="table-wrap">
              <table>
                <thead><tr><th scope="col">{t('bal.element')}</th><th scope="col">{t('bal.left')}</th><th scope="col">{t('bal.right')}</th><th scope="col"><span className="sr-only">OK</span></th></tr></thead>
                <tbody>
                  {result.atomTable.map((r) => (
                    <tr key={r.element}><th scope="row">{r.element}</th><td>{r.left}</td><td>{r.right}</td><td aria-label={r.left === r.right ? 'OK' : 'mismatch'}>{r.left === r.right ? '✓' : '✗'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted small">{t('bal.method')}</p>
            <SaveButton onSave={() => progress.recordExperiment('balancer', { e: result.equation })} />
          </>
        )}
      </section>
    </div>
  );
}
